import React, { useState, useEffect, useCallback, useMemo } from "react";
import debounceFunc from "lodash/debounce";
import { DebounceInput } from "react-debounce-input";
import { translate } from "../Services/translation";
import { post } from "../Services/http";
import { useDispatch, useSelector } from "react-redux";
import { catchError } from "../Actions/Error.action";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CART_LOAD_ERROR, setProductAdded } from "../Actions/Cart.action";
import { dataLayerAddToCart } from './../Services/Gtm.service';

const isTrue = (value) => value === true || value === "True" || value === "true";

export default function QuantityPurchaseContainer(props) {
    const {
        packageSize,
        allowSingleItem: allowSingleItemStr,
        articleNumber,
        productName,
        price,
        variant,
        brand,
        category,
        isKeg: isKegStr,
        isQuickSearch,
        isBlocked
    } = props;

    const allowSingleItem = useMemo(() => isTrue(allowSingleItemStr), [allowSingleItemStr]);
    const isKeg = useMemo(() => isTrue(isKegStr), [isKegStr]);

    const [count, setCount] = useState(undefined);
    const [previousValue, setPreviousValue] = useState(0);
    const toastError = (message) => toast.error(message);
    const dispatch = useDispatch();
    const cart = useSelector(state => state.cart);
    const maxCount = 999;
    const CART_RECEIVE = "CART_RECEIVE";
    const debounce = useCallback(
        debounceFunc((val, prevValue) => {
            let resultValue = val - prevValue;
            dispatch(
                addToCart({
                    articleNumber,
                    productName,
                    price,
                    variant,
                    brand,
                    category,
                    quantity: resultValue,
                })
            );
            if (val > maxCount) {
                val = maxCount;
            }
            setPreviousValue(val);
        }, 250),
        []
    );
    useEffect(() => {
        const orderRow = cart.orderRows.find(x => x.articleNumber == articleNumber);
        if (orderRow) {
            const quantity = orderRow.quantity;
            if (allowSingleItem) {
                setCount(quantity);
                setPreviousValue(quantity);
            } else {
                setCount(Math.ceil(quantity / packageSize));
                setPreviousValue(quantity);
            }
        }
        else {
            setCount(0);
        }

    }, []);
    const loadError = (error) => ({
        type: CART_LOAD_ERROR,
        payload: {
            error,
        },
    });
    const receive = (cart) => ({
        type: CART_RECEIVE,
        payload: cart,
    });

    const addToCart = ({
        articleNumber,
        name,
        price,
        variant,
        brand,
        category,
        quantity = 1,
    }) => {
        return (dispatch) => {
            if (quantity != 0) {
                if (!quantity || isNaN(quantity)) {
                    return;
                }
            }

            var changeCart = quantity > 1 ? "add_to_cart" : "remove_from_cart";
            if (typeof gtag !== 'undefined') {
                gtag("event", changeCart, {
                    items: [
                        {
                            id: articleNumber,
                            name: name,
                            brand: brand,
                            category: category,
                            variant: variant,
                            quantity: quantity,
                            price: price,
                            list_position: 1,
                        },
                    ],
                });
            }

            return post("/api/cart/add", {
                articleNumber,
                quantity: parseInt(quantity),
            })
                .then((response) => response.json())
                .then((cart) => {
                    dispatch(receive(cart));
                    if (cart.errors[articleNumber.toLowerCase()]) {
                        toastError(cart.errors[articleNumber.toLowerCase()]);
                    }
                    var cartObject = cart.orderRows.filter(
                        (x) => x.articleNumber == articleNumber
                    );
                    if (cartObject.length > 0) {
                        if (allowSingleItem) {
                            setCount(cartObject[0].quantity);
                            setPreviousValue(cartObject[0].quantity);
                        } else {
                            setCount(cartObject[0].quantity / packageSize);
                            setPreviousValue(cartObject[0].quantity);
                        }
                    }
                    dispatch(setProductAdded(true));
                    setTimeout(() => {
                        dispatch(setProductAdded(false));
                    }, 300);
                    if (!cart.errors[articleNumber.toLowerCase()]) {
                        const product = cart.orderRows.find(p => p.articleNumber == articleNumber);
                        if (product && product.gtmEcommerceItem) {
                            product.gtmEcommerceItem.quantity = parseInt(quantity);
                            dataLayerAddToCart([product.gtmEcommerceItem]);
                        }
                    }
                })
                .catch((ex) =>
                    dispatch(catchError(ex, (error) => loadError(error)))
                );
        };
    };

    const handleChange = (value) => {
        if (allowSingleItem) {
            if (
                count + parseInt(value) >= 0 &&
                count + parseInt(value) <= maxCount
            ) {
                debounce(count + parseInt(value), previousValue);
                setCount(count + parseInt(value));
            } else if (count + parseInt(value) > maxCount) {
                debounce(maxCount, previousValue);
                setCount(maxCount);
            }
        } else {
            if (
                count + parseInt(value) >= 0 &&
                count + parseInt(value) <= maxCount
            ) {
                let newValue = (count + parseInt(value)) * packageSize;
                debounce(newValue, previousValue);
                setCount(count + parseInt(value));
            } else if (count + parseInt(value) > maxCount) {
                debounce(maxCount, maxCount);
                setCount(maxCount);
            }
        }
    };
    function EnteredByKeys(value) {
        // Parse once, explicitly base-10
        const num = parseInt(value, 10);

        // Empty/invalid/negative
        if (value === '' || Number.isNaN(num) || num < 0) {
            debounce(0, previousValue);
            return setCount(0);
        }

        if (allowSingleItem) {
            // 3Clamp to [0 … maxCount]
            const clamped = Math.min(num, maxCount);

            debounce(clamped, previousValue);
            setCount(clamped);

        } else {
            // package-based logic
            const total = num * packageSize;
            const maxTotal = maxCount * packageSize;
            const clamped = Math.min(total, maxTotal);

            debounce(clamped, previousValue);
            setCount(num);
        }
    }
    return (
        <>
            {count != undefined && !isBlocked && <div className={count >= 1 ? "elements-are-visible" : "elements-not-visible"}>
                <div className="quantity-topic">
                    <div className="title">
                        {allowSingleItem || isKeg
                            ? translate("productinformation.singular")
                            : translate("productinformation.package")}
                    </div>
                </div>
                <div className="input-container small-12">
                    <div
                        className="decrease-btn"
                        onClick={(event) => handleChange(-1)}
                    >
                        <i className="fal fa-minus"></i>
                    </div>
                    <span className="package-quantity">
                        <DebounceInput
                            min="0"
                            max="999"
                            disabled={false}
                            type="number"
                            value={count == 0 ? "0" : count}
                            debounceTimeout={1000}
                            onChange={(event) =>
                                EnteredByKeys(event.target.value)
                            }
                        />
                    </span>
                    <div
                        className="increase-btn"
                        onClick={(event) => handleChange(1)}
                    >
                        <i className="fal fa-plus"></i>
                    </div>
                </div>
                {isQuickSearch &&
                    <div className="elements-are-visible" style={{ justifyContent: "inherit" }}>
                        <div className="package-topic">
                            {!allowSingleItem && packageSize > 1 && count > 0 &&
                                <div className="title">
                                    {!allowSingleItem && packageSize > 1 && count > 0 &&
                                        "= " +
                                        count * packageSize +
                                        " " +
                                        translate("productinformation.pcs")}
                                </div>
                            }
                            {allowSingleItem && !isKeg &&
                                <div className="title" style={{ height: "100%" }}>
                                    <span className="package-topic--icon-wrapper">
                                        <i className="package-topic--icon far fa-info-circle"></i>
                                        {translate("sellsingle.pcs")}
                                    </span>
                                </div>
                            }
                        </div>
                    </div>
                }
                {/* Only for listnings  */}
                {!isQuickSearch &&
                    <div className="package-topic">
                        <div className="title">
                            {packageSize > 1 &&
                                !allowSingleItem &&
                                "= " +
                                count * packageSize +
                                " " +
                                translate("productinformation.pcs")}
                        </div>
                    </div>}
            </div>}
            {/* Only for listnings  */}
            {!isQuickSearch &&
                <div className="elements-are-visible" style={{ justifyContent: "inherit" }}>
                    <div className="package-topic">
                        {allowSingleItem && !isKeg &&
                            <div className="title" style={{ height: "100%" }}>
                                <span className="package-topic--icon-wrapper">
                                    <i className="package-topic--icon far fa-info-circle"></i>
                                    {translate("sellsingle.pcs")}
                                </span>
                            </div>}
                    </div>
                </div>
            }
        </>
    );
}
